import React, { useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import { Execution } from '../../types';
import RecordExecution from './RecordExecution';

interface RecordExecutionsProps {
  executions: Execution[];
  onExecutionUpdated: (execution: Execution) => void;
}

const RecordExecutions: React.FC<RecordExecutionsProps> = ({ executions, onExecutionUpdated }) => {
  // Select by id, not by object: the executions array is replaced whenever the record
  // is refreshed, and holding on to the old object would pin a stale detail panel.
  const [selectedExecutionId, setSelectedExecutionId] = useState<number | null>(null);
  const selectedExecution = executions.find((e) => e.id === selectedExecutionId) ?? null;

  // Function to get the status badge color
  const getStatusBadgeVariant = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'started':
        return 'primary';
      case 'error':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Function to format date
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Handle execution selection
  const handleSelectExecution = (execution: Execution) => {
    setSelectedExecutionId(execution.id);
  };

  return (
    <Accordion className="mt-4">
      <Accordion.Item eventKey="0">
        <Accordion.Header>
          Executions
          {executions.length > 0 && (
            <Badge bg="info" className="ms-2">
              {executions.length}
            </Badge>
          )}
        </Accordion.Header>
        <Accordion.Body>
          {executions.length === 0 ? (
            <p className="text-muted">No executions found for this record.</p>
          ) : (
            <div className="row">
              <div className="col-md-3">
                <Card>
                  <Card.Header>Executions</Card.Header>
                  <ListGroup variant="flush">
                    {executions.map((execution) => (
                      <ListGroup.Item
                        key={execution.id}
                        action
                        active={selectedExecution?.id === execution.id}
                        onClick={() => handleSelectExecution(execution)}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <div title={String(execution.id)}>{execution.type}</div>
                          <small className="text-muted">
                            {formatDate(execution.start_date)}
                          </small>
                        </div>
                        <Badge bg={getStatusBadgeVariant(execution.status)}>
                          {execution.status}
                        </Badge>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card>
              </div>
              <div className="col-md-9">
                {selectedExecution ? (
                  <RecordExecution
                    execution={selectedExecution}
                    onExecutionUpdated={onExecutionUpdated}
                  />
                ) : (
                  <Card>
                    <Card.Body className="text-center text-muted">
                      Select an execution to view details
                    </Card.Body>
                  </Card>
                )}
              </div>
            </div>
          )}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default RecordExecutions;
